/**
 * ExecOperationsTiles — Iter-90 · CEO Executive HQ read-only rollup.
 *
 * Two tiles pulled from `GET /api/operations/exec/summary`:
 *   1. Active Routes Today · X on-track / Y delayed
 *   2. Gemini AI Accept Rate · current week % + WoW trend
 *
 * Full-fidelity map + trend chart live inside the Operations Department's
 * "Live Ops" tab — this exec view is intentionally summary-only per the
 * architecture rule.
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { MapPin, Sparkles, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ExecOperationsTiles() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/operations/exec/summary`, { credentials: 'include' });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setData(d);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const routes = data?.active_routes || { total: 0, on_track: 0, delayed: 0, delayed_today_total: 0 };
  const ai = data?.ai_accuracy || { current_week_accept_rate: 0, trend: 'flat', wow_delta_pts: 0, low_confidence_rate: 0, ungraded_notes_count: 0 };
  const TrendIcon = ai.trend === 'up' ? ArrowUpRight
                   : ai.trend === 'down' ? ArrowDownRight : Minus;
  const trendColor = ai.trend === 'up' ? '#10b981'
                    : ai.trend === 'down' ? '#ef4444' : '#a1a1aa';

  const goToLiveOps = () => navigate('/company-manager-dashboard?tab=live-ops');

  return (
    <section data-testid="exec-operations-tiles">
      <h2 className="text-xs uppercase tracking-widest text-[#FFD60A] mb-3 font-semibold">
        Operations Intelligence · Live
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={goToLiveOps}
          className="text-left bg-zinc-900 border-2 rounded-lg p-4 transition hover:-translate-y-0.5"
          style={{ borderColor: routes.delayed > 0 ? 'rgba(239,68,68,0.5)' : 'rgba(255,214,10,0.4)' }}
          data-testid="exec-tile-active-routes"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(255,214,10,0.15)', color: '#FFD60A' }}>
              <MapPin className="h-4 w-4" />
            </span>
            <p className="text-sm font-bold text-white uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Active Routes Today
            </p>
          </div>
          <p className="text-3xl font-black text-white leading-none">{routes.total}</p>
          <div className="mt-2 text-[11px] flex items-center gap-3 flex-wrap">
            <span className="text-emerald-400 font-semibold" data-testid="exec-tile-on-track">
              {routes.on_track} on-track
            </span>
            <span className="text-red-400 font-semibold" data-testid="exec-tile-delayed">
              {routes.delayed} delayed
            </span>
            <span className="text-zinc-400" data-testid="exec-tile-delayed-today">
              Today: <b className="text-white">{routes.delayed_today_total}</b> delay alerts
            </span>
            <span className="ml-auto text-zinc-500">Open full map →</span>
          </div>
        </button>

        <button
          type="button"
          onClick={goToLiveOps}
          className="text-left bg-zinc-900 border-2 rounded-lg p-4 transition hover:-translate-y-0.5"
          style={{ borderColor: 'rgba(245,158,11,0.4)' }}
          data-testid="exec-tile-ai-accuracy"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>
              <Sparkles className="h-4 w-4" />
            </span>
            <p className="text-sm font-bold text-white uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Gemini AI Accept Rate
            </p>
          </div>
          <p className="text-3xl font-black text-white leading-none" data-testid="exec-tile-ai-rate">
            {ai.current_week_accept_rate}%
          </p>
          <div className="mt-2 text-[11px] flex items-center gap-2 flex-wrap" style={{ color: trendColor }}>
            <TrendIcon className="h-3.5 w-3.5" />
            <span className="font-semibold">
              {ai.wow_delta_pts > 0 ? '+' : ''}{ai.wow_delta_pts}pts week-over-week
            </span>
            <span className="text-red-400 font-semibold" data-testid="exec-tile-low-conf">
              Low-conf: {ai.low_confidence_rate}%
            </span>
            <span className="text-purple-300 font-semibold" data-testid="exec-tile-ungraded">
              Ungraded: {ai.ungraded_notes_count}
            </span>
            <span className="ml-auto text-zinc-500">View trend →</span>
          </div>
        </button>
      </div>
    </section>
  );
}
